import { useId, useRef, useState, type FormEvent } from "react";
import type {
  CreateWholesaleCustomerInput,
  UpdateWholesaleCustomerInput,
  WholesaleCustomerView,
} from "../../../shared/contracts.js";
import { LIMITS } from "../../../shared/contracts.js";
import { normalizeName } from "../../../shared/formatting.js";
import { fieldErrorsOf, orderMutationErrorMessage } from "../../api/caseMutations.js";
import { isClientApiError } from "../../api/http.js";
import { useCreateCustomer, useUpdateCustomer } from "../../api/wholesaleQueries.js";
import { Button } from "../../ui/Button.js";
import { Dialog, DialogActions } from "../../ui/Dialog.js";
import { FormField, TextField } from "../../ui/FormField.js";

/**
 * The customer directory form (spec §3.1, §4.6): the business name (the
 * one required field, unique among active customers), the contact, phone,
 * email and notes. Opened from the Customers page to add or edit, and
 * from the wholesale order form's inline "Add customer", which returns
 * with the new customer selected.
 *
 * Adding sends only the fields that were typed; editing sends only what
 * changed, with `null` for a cleared field, under the customer's version
 * — a 409 `STALE_WRITE` says so and the caller reloads. A 409
 * `CUSTOMER_NAME_IN_USE` lands on the name field in the server's words;
 * a 422 beside the field it names.
 */

export type CustomerFormValues = {
  name: string;
  contactName: string;
  phone: string;
  email: string;
  notes: string;
};

export type CustomerFormErrors = Partial<Record<keyof CustomerFormValues, string>>;

export const CUSTOMER_FORM_MESSAGES = {
  name: "Enter the business name.",
  nameLength: `Use ${LIMITS.name.min}–${LIMITS.name.max} characters.`,
  email: "Enter a valid email address.",
  notes: `Use up to ${LIMITS.note.max} characters.`,
} as const;

const FIELD_KEYS: readonly (keyof CustomerFormValues)[] = ["name", "contactName", "phone", "email", "notes"];

export function emptyCustomerForm(): CustomerFormValues {
  return { name: "", contactName: "", phone: "", email: "", notes: "" };
}

export function customerFormFrom(customer: WholesaleCustomerView): CustomerFormValues {
  return {
    name: customer.name,
    contactName: customer.contactName ?? "",
    phone: customer.phone ?? "",
    email: customer.email ?? "",
    notes: customer.notes ?? "",
  };
}

/** The browser's check before the server's: a name, and an email that looks like one when typed. */
export function validateCustomerForm(values: CustomerFormValues): CustomerFormErrors {
  const errors: CustomerFormErrors = {};
  const name = normalizeName(values.name);
  if (name.length === 0) errors.name = CUSTOMER_FORM_MESSAGES.name;
  else if (name.length < LIMITS.name.min || name.length > LIMITS.name.max) errors.name = CUSTOMER_FORM_MESSAGES.nameLength;
  const email = values.email.trim();
  if (email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errors.email = CUSTOMER_FORM_MESSAGES.email;
  if (values.notes.trim().length > LIMITS.note.max) errors.notes = CUSTOMER_FORM_MESSAGES.notes;
  return errors;
}

function blankToUndefined(value: string): string | undefined {
  const trimmed = value.trim();
  return trimmed === "" ? undefined : trimmed;
}

/** `POST /api/wholesale/customers` from the form: blank optional fields are left out. */
export function toCreateCustomerInput(values: CustomerFormValues): CreateWholesaleCustomerInput {
  const contactName = blankToUndefined(values.contactName);
  const phone = blankToUndefined(values.phone);
  const email = blankToUndefined(values.email);
  const notes = blankToUndefined(values.notes);
  return {
    name: values.name.trim(),
    ...(contactName !== undefined ? { contactName } : {}),
    ...(phone !== undefined ? { phone } : {}),
    ...(email !== undefined ? { email } : {}),
    ...(notes !== undefined ? { notes } : {}),
  };
}

/** The fields whose value differs from the customer on record; a cleared optional field travels as `null`. */
export function customerPatch(baseline: CustomerFormValues, values: CustomerFormValues): UpdateWholesaleCustomerInput["patch"] {
  const patch: { -readonly [K in keyof UpdateWholesaleCustomerInput["patch"]]: UpdateWholesaleCustomerInput["patch"][K] } = {};
  if (normalizeName(values.name) !== normalizeName(baseline.name)) patch.name = values.name.trim();
  for (const key of ["contactName", "phone", "email", "notes"] as const) {
    const next = values[key].trim();
    if (next !== baseline[key].trim()) patch[key] = next === "" ? null : next;
  }
  return patch;
}

export interface CustomerDialogProps {
  open: boolean;
  onClose: () => void;
  /** The customer to edit; absent when adding. */
  customer?: WholesaleCustomerView | null;
  /** Called with the server's customer once it is stored. */
  onSaved: (customer: WholesaleCustomerView) => void;
}

export function CustomerDialog(props: CustomerDialogProps) {
  if (!props.open) return null;
  return <CustomerForm {...props} />;
}

function CustomerForm({ onClose, customer = null, onSaved }: CustomerDialogProps) {
  const editing = customer !== null;
  const idPrefix = useId();
  const nameRef = useRef<HTMLInputElement>(null);
  const [baseline] = useState<CustomerFormValues>(() => (customer ? customerFormFrom(customer) : emptyCustomerForm()));
  const [values, setValues] = useState<CustomerFormValues>(baseline);
  const [errors, setErrors] = useState<CustomerFormErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const create = useCreateCustomer();
  const update = useUpdateCustomer();
  const pending = create.isPending || update.isPending;

  const id = (field: keyof CustomerFormValues) => `${idPrefix}-${field}`;

  const change = (field: keyof CustomerFormValues, value: string) => {
    setValues((current) => ({ ...current, [field]: value }));
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));
    if (formError) setFormError(null);
  };

  const onFailure = (error: unknown) => {
    if (isClientApiError(error) && error.status === 409 && error.code === "CUSTOMER_NAME_IN_USE") {
      setErrors((current) => ({ ...current, name: error.message }));
      nameRef.current?.focus();
      return;
    }
    const fieldErrors = fieldErrorsOf(error);
    if (fieldErrors) {
      const mapped: CustomerFormErrors = {};
      const unmapped: string[] = [];
      for (const [path, messages] of Object.entries(fieldErrors)) {
        const message = messages.find((text) => text.length > 0) ?? "Check this field.";
        const key = (path.startsWith("patch.") ? path.slice("patch.".length) : path) as keyof CustomerFormValues;
        if (FIELD_KEYS.includes(key)) mapped[key] = message;
        else unmapped.push(message);
      }
      setErrors(mapped);
      setFormError(unmapped.length > 0 ? unmapped.join(" ") : null);
      return;
    }
    setFormError(orderMutationErrorMessage(error));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (pending) return;
    const validation = validateCustomerForm(values);
    setErrors(validation);
    setFormError(null);
    if (Object.keys(validation).length > 0) {
      const first = FIELD_KEYS.find((key) => validation[key]);
      if (first) document.getElementById(id(first))?.focus();
      return;
    }
    if (!customer) {
      create.mutate(toCreateCustomerInput(values), {
        onSuccess: (created) => {
          onSaved(created);
          onClose();
        },
        onError: onFailure,
      });
      return;
    }
    const patch = customerPatch(baseline, values);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    update.mutate(
      { customerId: customer.id, input: { patch, expectedVersion: customer.version } },
      {
        onSuccess: (updated) => {
          onSaved(updated);
          onClose();
        },
        onError: onFailure,
      },
    );
  };

  return (
    <Dialog
      open
      onClose={onClose}
      title={editing ? "Edit customer" : "Add customer"}
      description={
        editing
          ? "Changes show on every order that names this customer. The name must stay unique among active customers."
          : "A wholesale customer — a body shop, an independent or another dealer. Only the business name is required."
      }
      pending={pending}
      initialFocusRef={nameRef}
      size="medium"
    >
      <form className="order-form order-form--sheet customer-form" noValidate onSubmit={submit}>
        <div className="order-form__grid">
          <TextField
            ref={nameRef}
            id={id("name")}
            label="Business name"
            error={errors.name ?? null}
            value={values.name}
            onChange={(event) => change("name", event.target.value)}
            autoComplete="organization"
            maxLength={LIMITS.name.max + 10}
            disabled={pending}
            fieldClassName="order-form__field--wide"
          />
          <TextField
            id={id("contactName")}
            label="Contact name"
            optional
            error={errors.contactName ?? null}
            value={values.contactName}
            onChange={(event) => change("contactName", event.target.value)}
            autoComplete="off"
            maxLength={LIMITS.name.max + 10}
            disabled={pending}
          />
          <TextField
            id={id("phone")}
            label="Phone"
            type="tel"
            optional
            error={errors.phone ?? null}
            value={values.phone}
            onChange={(event) => change("phone", event.target.value)}
            autoComplete="off"
            maxLength={LIMITS.phone.max + 10}
            disabled={pending}
          />
          <TextField
            id={id("email")}
            label="Email"
            type="email"
            optional
            hint="Needed for Email customer on an arrived order."
            error={errors.email ?? null}
            value={values.email}
            onChange={(event) => change("email", event.target.value)}
            autoComplete="off"
            spellCheck={false}
            disabled={pending}
            fieldClassName="order-form__field--wide"
          />
          <FormField id={id("notes")} label="Notes" optional error={errors.notes ?? null} className="order-form__field--wide">
            {(control) => (
              <textarea
                {...control}
                className="text-input textarea-input"
                data-size="desktop"
                rows={3}
                value={values.notes}
                onChange={(event) => change("notes", event.target.value)}
                maxLength={LIMITS.note.max + 10}
                disabled={pending}
              />
            )}
          </FormField>
        </div>
        {formError ? (
          <p role="alert" className="form-alert order-form__alert">
            {formError}
          </p>
        ) : null}
        <DialogActions>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" busy={pending}>
            {editing ? "Save changes" : "Add customer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
